import { useMemo, useState } from "react";
import { ButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import { DefaultButton } from "../../common/components/DefaultButton";
import { Col, Row } from "../../common/components/Grid";
import Select from "../../common/components/Select";
import { SettingsDisplay, useSettingsContext } from "../../common/components/SettingsPanels";
import { SortDialog } from "../../common/components/Sort";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { cdate, now } from "../../common/utils/CDate";
import { ReactNodeDialog } from "../../common/utils/dialogUtlities";
import { dynamicFilter, dynamicSort } from "../../common/utils/tableUtilities";
import { addEventActiveTime, canCreateEvent, canRemoveEvent, canUpdateEvent, createEvent, removeEvent, removeEventActiveTime, updateEvent, useEvents } from "../../reducers/eventSlice";
import { EventDetails } from "../EventDetails";
import PropTypes from "prop-types";
import { deepClone } from "../../common/utils/objectUtilities";

const EventButton = ({event, selected, selectEvent, setNewEvent}) => {
    const { t } = useTranslation();
    const { pushPanel } = useSettingsContext();
    const clientConfig = useClientConfig();
   
    const nameSize = clientConfig.enableSites && clientConfig.enableLevels ? 3 : (clientConfig.enableSites || clientConfig.enableLevels ? 4 : 6);

    const displayActivity = useMemo(() => {
        let activeTimes = deepClone(event.active ?? []);
        activeTimes.sort(dynamicSort(['start']));
        let idx = 0;
        while(idx < activeTimes.length && cdate(activeTimes[idx].end).isBefore(now())) {
            ++idx;
        }
        if (idx >= activeTimes.length) {
            return [];
        } else {
            return activeTimes.slice(idx, idx + 3);
        }
    }, [event.active])
   
   return <Button key={'event_edit_btn_' + event.id} className="m-2" variant={selected ? "dark" : "outline-dark"} onClick={() => { pushPanel(1, setNewEvent); selectEvent(); }}>
        <Row className="align-items-center">
        <Col>
                <Row className="text-left align-items-center">
                    <Col style={{ whiteSpace: 'normal', }} md={nameSize}>{event.name} </Col>
                    {clientConfig.enableSites ? <Col style={{ whiteSpace: 'normal'}} md={clientConfig.enableLevels ? 3 : 4}>{event.site}</Col> : null}
                    {clientConfig.enableLevels ? <Col style={{ whiteSpace: 'normal'}} md={clientConfig.enableSites ? 3 : 4}>{event.level}</Col> : null}
                    {<Col style={{ whiteSpace: 'normal'}} md={nameSize}>{event.type}</Col>}
                </Row>
            </Col>
            { displayActivity.length > 0 && <Col xs="auto">
                {displayActivity.map(a => <Row><Col>{cdate(a.start, clientConfig.tz).dateTimeNoYearFormat()}</Col><Col className="mx-1">-</Col><Col>{cdate(a.end, clientConfig.tz).dateTimeNoYearFormat()}</Col></Row>)}
            </Col> }
            { (displayActivity.length === 0 && event.active?.length > 0) && <Col xs="auto">
                {t('complete')}
            </Col> }
        </Row>
    </Button>
}

const defaultSort = ['name'];

const NewEventButton = ({selected, onClick}) => {
    const { pushPanel } = useSettingsContext();
    return <DefaultButton className="m-2" kind="add" variant={selected ? "primary" : "outline-primary"} onClick={() => { pushPanel(1); onClick() }}></DefaultButton>
}

const newEventTemplate = { customfields: {}, active: [] }

const Events = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const credentials = useCredentials();
    const events = useEvents();
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState({ status: 'Upcoming' });
    const [newEvent, setNewEvent] = useState(deepClone(newEventTemplate));
    const [selectedEvent, setSelectedEvent] = useState();
    const isNew = !selectedEvent;
    const [showSortDialog, setShowSortDialog] = useState(false);
    const [editFilter, setEditFilter] = useState();
    const [sort, setSort] = useState(defaultSort);

    const currentEvent = useMemo(() => isNew ? newEvent : events.find(e => e.id === selectedEvent), [isNew, newEvent, events, selectedEvent]);

    const sortFields = useMemo(() => {
        let fields = { name: t('name') }
        if (clientConfig.enableLevels) {
            fields.level = t('level_one');
        }
        if (clientConfig.enableSites) {
            fields.site = t('site_one');
        }
        fields.type = t('type_one');
        return fields;
    }, [clientConfig.enableLevels, clientConfig.enableSites, t])
    
    let displayEvents = useMemo(() => {
        let displayEvents = deepClone(events);
        displayEvents.sort(dynamicSort(sort));
        if (query) {
            displayEvents = dynamicFilter(displayEvents, query);
        }
        if (filter.status === 'Upcoming') {
            displayEvents = displayEvents.filter(event => !event.active || event.active.some(a => new Date (a.end) > new Date()));
        } else if (filter.status === 'Complete') {
            displayEvents = displayEvents.filter(event => event.active && event.active.every(a => new Date(a.end) <= new Date()));
        }
        return displayEvents;
    }, [events, query, sort, filter]);

    const disabled = useMemo(() =>  isNew ? !canCreateEvent(credentials, currentEvent) : !canUpdateEvent(credentials, currentEvent), [credentials, currentEvent, isNew]);

    const setupNewEvent = () => {
        setSelectedEvent();
        setNewEvent(deepClone(newEventTemplate));
    }
    
    const handleUpdate = delta => {
        if (isNew) {
            if (delta.customfields) {
                setNewEvent(newEvent => ({...newEvent, ...delta, customfields: {...newEvent.customfields, ...delta.customfields}}));
            } else {
                setNewEvent(newEvent => ({...newEvent, ...delta}));
            }
        } else {
            updateEvent(network, {id: currentEvent.id, ...delta})
        }
    }

    const handleTypeUpdate = type => {
        let delta = { type: type.type };
        if (type.site) {
            delta.site = type.site;
        } 
        if (type.level) {
            delta.level = type.level
        }
        handleUpdate(delta);
    }

    const handleCreate = () => {
        createEvent(network, newEvent);
        setNewEvent(deepClone(newEventTemplate));
    }

    const handleDelete = () => {
        removeEvent(network, currentEvent.id);
        setupNewEvent();
    }

    const onActiveTimeAdd = isNew ? undefined : row => addEventActiveTime(network, {...row, eventid: currentEvent.id});

    const onActiveTimeRemove = isNew ? undefined :  row => removeEventActiveTime(network, row.id);
    
    const onActiveTimeChange = isNew ? data => handleUpdate({ active: data }) : undefined;

    const handleCopy = () => {
        const newEvent = deepClone(currentEvent);
        delete newEvent.id;
        if (newEvent.active?.length > 0) {
            newEvent.active.forEach(a => delete a.id);
        }
        setNewEvent(newEvent);
        setSelectedEvent();
    }


    const controls = <Row className="mx-2">
        <Col xs='auto'>
            {isNew ? (disabled ? null : <DefaultButton kind="clear" onClick={setupNewEvent}></DefaultButton>) : (canRemoveEvent(credentials, currentEvent) ? <DefaultButton kind="delete" confirmMessage={t('confirmDeleteEvent', currentEvent)} onClick={handleDelete}></DefaultButton> : null)}
        </Col>
        <Col></Col>
        <Col xs='auto'>
            {isNew && canCreateEvent(credentials, currentEvent) && <DefaultButton kind="add" disabled={!currentEvent.name} onClick={handleCreate}></DefaultButton>}
            {!isNew && canCreateEvent(credentials, currentEvent) && <DefaultButton kind="copy" onClick={handleCopy}></DefaultButton>}
        </Col>
    </Row>

    const filterOptions = useMemo(() => [{ value: "Upcoming", label: t('upcoming') }, {value: "Complete", label: t('complete')}], [t]);

    return <>
        <SortDialog show={showSortDialog} defaultOrder={defaultSort} sort={sort} fields={sortFields} title={t('userSort')} onClose={newSort => { if (newSort) { setSort(newSort); } setShowSortDialog(false); }} placeholder={t('sort')}></SortDialog>
        <ReactNodeDialog title={t('filterConfig')} showModal={editFilter !== undefined} onSave={() => { setFilter(editFilter); setEditFilter(); }} onClose={() => setEditFilter()}>
            <Select value={filter.status} placeholder={t('status_one')} onChange={v => setEditFilter(filter => ({ ...filter, status: v }))} wildcard={t('anyWildcard')} options={filterOptions} labelField="label" valueField="value"></Select>
        </ReactNodeDialog>
        <SettingsDisplay uuid={uuid} title={label} onClose={onClose}>
            <div className="w-100 h-100 d-flex flex-column">
                <Row>
                    <Col>
                        <TextInput value={query} live={true} hideLabel={true} placeholder={t('filter', { count: 1 })} onChange={setQuery}
                            prependCtrl={
                                <ButtonGroup>
                                    <DefaultButton kind="sort" onClick={() => setShowSortDialog(true)} hideLabel={true}></DefaultButton>
                                    <DefaultButton kind="filter" variant={ filter.status ? 'success' : 'default' } onClick={() => setEditFilter({ ...filter })} hideLabel={true}></DefaultButton>
                                </ButtonGroup>
                            }>
                        </TextInput>
                    </Col>
                </Row>
                {canCreateEvent(credentials) ? <NewEventButton onClick={setupNewEvent} selected={isNew}></NewEventButton> : null}
                <div className="d-flex flex-column h-100 w-100" style={{ flex: 1, overflowY: 'auto'}}>
                    {displayEvents.map(e => <EventButton key={e.id} event={e} selectEvent={() => setSelectedEvent(e.id)} selected={selectedEvent === e.id} setNewEvent={setupNewEvent}></EventButton>)}
                </div>
            </div>
            <div className="ml-2 mr-0 h-100 d-flex flex-column">
                {controls}
                <div className="mt-2" style={{ flex: 1, overflowY: 'auto' }}>
                    <EventDetails event={currentEvent} disabled={disabled} onChange={handleUpdate} handleTypeUpdate={handleTypeUpdate} onActiveTimeAdd={onActiveTimeAdd} onActiveTimeChange={onActiveTimeChange} onActiveTimeRemove={onActiveTimeRemove}></EventDetails>
                </div>
            </div>
        </SettingsDisplay>
        </>
}
Events.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}
export default Events